import { Mail, Wrench, Bug, CreditCard, Users, MessageCircleQuestion } from "lucide-react";
import LegalPageShell from "@/components/site/LegalPageShell";
import { useLanguage, pick } from "@/lib/i18n";
import { SUPPORT_EMAIL } from "@/lib/translations";

const COPY = {
    en: {
        title: "Contact & Support",
        subtitle:
            "We are a small, family-friendly studio and we love hearing from parents, teachers and curious kids alike.",
        emailLabel: "Email us directly at",
        responseTitle: "Response time",
        responseBody: "We aim to respond within 48 hours, usually faster on weekdays.",
        topicsTitle: "What we can help with",
        topics: [
            { icon: Wrench, title: "Technical support", desc: "Crashes, installation issues, restoring purchases, account questions." },
            { icon: CreditCard, title: "Premium purchase support", desc: "Help with the one-time premium upgrade, receipts and refunds via Google Play." },
            { icon: Bug, title: "Bug reporting", desc: "Found something odd? Tell us what happened and on which device — screenshots welcome." },
            { icon: Users, title: "Parent inquiries", desc: "Questions about privacy, child safety, age suitability and content choices." },
            { icon: MessageCircleQuestion, title: "General questions", desc: "Anything else — partnership ideas, press, feedback or a friendly hello." },
        ],
        privacyNote:
            "Please do not include sensitive personal data (such as full address, payment details or your child’s personal information) in your first email. We will only ask for what is strictly necessary to help.",
    },
    de: {
        title: "Kontakt & Support",
        subtitle:
            "Wir sind ein kleines, familienfreundliches Studio und freuen uns über Nachrichten von Eltern, Lehrkräften und neugierigen Kindern.",
        emailLabel: "Schreib uns direkt an",
        responseTitle: "Antwortzeit",
        responseBody: "Wir antworten in der Regel innerhalb von 48 Stunden, an Wochentagen oft schneller.",
        topicsTitle: "Wobei wir helfen",
        topics: [
            { icon: Wrench, title: "Technischer Support", desc: "Abstürze, Installationsprobleme, Käufe wiederherstellen, Kontofragen." },
            { icon: CreditCard, title: "Hilfe beim Premium-Kauf", desc: "Unterstützung beim einmaligen Premium-Upgrade, Belege und Erstattungen via Google Play." },
            { icon: Bug, title: "Fehler melden", desc: "Etwas Seltsames entdeckt? Beschreibe was passiert ist und auf welchem Gerät — gerne mit Screenshots." },
            { icon: Users, title: "Anfragen von Eltern", desc: "Fragen zu Datenschutz, Kinderschutz, Altersfreigabe und Inhalten." },
            { icon: MessageCircleQuestion, title: "Allgemeine Fragen", desc: "Kooperationen, Presse, Feedback oder einfach ein freundliches Hallo." },
        ],
        privacyNote:
            "Bitte sende uns in deiner ersten E-Mail keine sensiblen personenbezogenen Daten (z. B. vollständige Adresse, Zahlungsdaten oder Daten deines Kindes). Wir fragen nur das ab, was zur Unterstützung wirklich nötig ist.",
    },
};

export default function Contact() {
    const { lang } = useLanguage();
    const t = pick(COPY, lang);

    return (
        <LegalPageShell title={t.title} subtitle={t.subtitle}>
            <div className="not-prose text-center mb-10">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-500 text-jungle-900 shadow-playful-sm mb-5">
                    <Mail className="w-8 h-8" strokeWidth={2.5} />
                </div>
                <p className="font-display uppercase tracking-wider text-xs text-ink-300 mb-2">
                    {t.emailLabel}
                </p>
                <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    data-testid="contact-email"
                    className="inline-block font-display text-2xl sm:text-3xl font-bold text-jungle-700 hover:text-amber-600 transition-colors break-all"
                >
                    {SUPPORT_EMAIL}
                </a>
            </div>

            <div className="not-prose rounded-2xl bg-amber-500/15 border-2 border-amber-500/30 p-5 mb-10">
                <p className="font-display font-semibold text-jungle-900 mb-1">⏱ {t.responseTitle}</p>
                <p className="text-ink-700 text-sm">{t.responseBody}</p>
            </div>

            <h2>{t.topicsTitle}</h2>
            <div className="not-prose grid sm:grid-cols-2 gap-4 my-6">
                {t.topics.map((topic) => {
                    const Icon = topic.icon;
                    return (
                        <div
                            key={topic.title}
                            data-testid={`contact-topic-${topic.title.toLowerCase().replace(/\s+/g, "-")}`}
                            className="rounded-2xl border-2 border-jungle-700/10 bg-cream-100 p-5 hover:bg-white hover:border-jungle-700/30 transition-colors"
                        >
                            <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-jungle-700 text-cream-50 mb-3">
                                <Icon className="w-5 h-5" strokeWidth={2.5} />
                            </div>
                            <div className="font-display font-semibold text-jungle-900">{topic.title}</div>
                            <p className="text-sm text-ink-500 mt-1 leading-relaxed">{topic.desc}</p>
                        </div>
                    );
                })}
            </div>

            <p className="text-sm italic">{t.privacyNote}</p>
        </LegalPageShell>
    );
}
